import { SeverityEnum } from './general.interface';

export interface SystemSecurityScore {
  covered: number;
  completed: number;
  newAdded: number;
  severity: SeverityEnum;
  target: number;
}

export interface SecurityPlanRiskCoverage {
  coveredRisks: number;
  completedInProgram: number;
  newCovered: number;
  existingCovered: number;
  uncompletedRisks: number;
  totalRisks: number;
  percentageCovered: number;
}

export interface ContractualCompliance {
  frameworkName: string;
  frameworkId: string;
  isNA: boolean;
  // unified
  totalControls: number;
  covered: number;
  existingCovered: number;
  newCovered: number;
  uncompleted: number;
  completedInProgram: number;
  percentageCovered: number;
}

export interface CriticalSystems {
  financial: ProgressMetric;
  coreBusiness: ProgressMetric;
  ot: ProgressMetric;
  infrastructure: ProgressMetric;
}

export interface SecurityPlan {
  id: string;
  name: string;
  operationStartDate: string;
  operationEndDate: string;
  planLength: number;
  complianceCoverage: CoverageMetrics;
  riskCoverage: CoverageMetrics;
  systemScore: SecurityScore;
  budget: number;
  status: PlanStatus;
  budgetType?: SecurityPlanBudgetType;
  presetType?: SecurityPlanPresetBudgetType;
  showGreenBorder?: boolean;
  showRedBorder?: boolean;
}

export interface PlanStatusDropdownOption {
  id: PlanStatus;
  name: string;
  description: string;
  isSelectable: boolean;
}

export enum PlanStatus {
  DRAFT = 1,
  APPROVED,
  IN_PROGRESS,
  COMPLETED,
}

export interface CoverageMetrics {
  total: number;
  covered: number;
  existingCovered: number;
  newCovered: number;
  uncompleted: number;
  completedInProgram: number;
}

export interface SecurityScore {
  previousScore: number;
  projectedScore: number;
}

export interface ProgressMetric {
  name: string;
  covered: number;
  completed: number;
  isNA?: boolean;
}

export interface ComplianceCoverage {
  frameworks: ContractualCompliance[];
}

export interface LocalLawsProtection {
  dataTypes: LocalLawsProtectionItem[];
}

export interface LocalLawsProtectionItem {
  dataTypeName: string;
  dataTypeId: string;
  isNA: boolean;
  // unified
  totalControls: number;
  covered: number;
  existingCovered: number;
  newCovered: number;
  uncompleted: number;
  completedInProgram: number;
  percentageCovered: number;
  // legacy — delete after backend removes these fields
  coveredControls: number;
  uncompletedControls: number;
  currentlyCovered: number;
}

export const PlanStatusLabels = {
  [PlanStatus.DRAFT]: 'Draft',
  [PlanStatus.IN_PROGRESS]: 'In Progress',
  [PlanStatus.COMPLETED]: 'Completed',
  [PlanStatus.APPROVED]: 'Approved',
};

export interface SecurityPlanDetails {
  id?: string;
  name: string;
  startingDate: string;
  planLength: number;
  budgetType: SecurityPlanBudgetType;
  budgetAmount: number;
  presetType: SecurityPlanPresetBudgetType;
  status: SecurityPlanDetailsStatus;
  /** Remediation / plan strategy selection when provided by the API. */
  selectedStrategyOptionId?: StrategyOption;
  selectedControlPrioritizationOptionId: ControlPrioritizationOption;
}

export enum ControlPrioritizationOption {
  BEST_IN_RISK = 1,
  BEST_FOR_ORG = 2,
}

export enum StrategyOption {
  MAX_AFFECT = 1,
  MIN_COST = 2,
}

export interface SecurityPlanDetailsStatus {
  id: number;
  name: string;
}

export interface CreateUpdateSecurityPlan {
  id?: string;
  name: string;
  startingDate: string;
  planLength: number;
  budgetTypeId: number;
  budgetAmount?: number;
  presetTypeId?: number;
  statusId: number;
  selectedStrategyOptionId?: StrategyOption;
  selectedControlPrioritizationOptionId?: ControlPrioritizationOption;
  resetOutOfScopeDates?: boolean;
}

export interface SecurityPlanBudgetType {
  id: SecurityPlanBudgetTypeEnum;
  name: string;
}

export enum SecurityPlanBudgetTypeEnum {
  PRE_SET = 1,
  ACCUMULATING,
}

export enum SecurityPlanPresetBudgetTypeEnum {
  YEARLY = 1,
  QUARTERLY,
}

export interface SecurityPlanPresetBudgetType {
  id: SecurityPlanPresetBudgetTypeEnum;
  name: string;
}

export interface DatesOverlap {
  status: PlanStatusDropdownOption;
  conflicts: {
    planId: string;
    name: string;
    startingDate: string;
    endDate: string;
  }[];
  messageId: string;
  hasOverlap: boolean;
}

export interface PlanStrategyOption<T> {
  id: T;
  /** Canonical English name (Task 2531 T177 / M4A). */
  name: string;
  nameHe?: string;
  nameAr?: string;
  description: string;
  descriptionHe?: string;
  descriptionAr?: string;
}
